import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    CashAuthRequest, CashAuthHistoryRow, CashAuthOutcome,
    getApprovalQueue, getApprovalHistory, approveCashRequest, declineCashRequest,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../common/ConfirmDialog";
import { can } from "../../utils/roles";
import { FaLock, FaSyncAlt, FaCheck, FaTimes, FaClock, FaHistory } from "react-icons/fa";

/**
 * Cash sales waiting on head office.
 *
 * An agent reaches this queue only after the code has genuinely failed to
 * arrive. Somebody is usually standing at a counter while a request sits here,
 * so the list refreshes itself and shows how long is left before each one
 * expires.
 *
 * Approving is a way past the *code*, not past the daily limit. If the agent is
 * already at their limit the server refuses and says so; raising the limit or
 * resetting their day is the deliberate act that lets it through.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

/** Refresh often enough that a waiting agent is not left guessing. */
const POLL_MS = 15000;

const money = (amount: number | null, currency: string) =>
    amount === null ? "—" : `${currency} ${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const minutesLeft = (expiresAt: string) => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const mins = Math.ceil(ms / 60000);
    return `${mins} min left`;
};

const waitingFor = (requestedAt: string) => {
    const mins = Math.floor((Date.now() - new Date(requestedAt).getTime()) / 60000);
    if (mins < 1) return "just now";
    return `${mins} min ago`;
};

/** Absolute, not relative. History is read long after the fact. */
const when = (iso: string | null) =>
    iso
        ? new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        })
        : "—";

/** How many rows a page of history holds. */
const HISTORY_PAGE = 20;

const HISTORY_FILTERS: { value: CashAuthOutcome | "all"; label: string }[] = [
    { value: "all", label: "All" },
    { value: "approved", label: "Approved" },
    { value: "declined", label: "Turned down" },
    { value: "expired", label: "Expired" },
];

/**
 * Expired is deliberately not red. Nobody did anything wrong — the request
 * simply outlived its window, and that is worth seeing without being alarming.
 */
const OUTCOME_STYLE: Record<CashAuthOutcome, { label: string; tone: string }> = {
    approved: { label: "Approved", tone: "bg-green-100 text-green-800" },
    declined: { label: "Turned down", tone: "bg-red-100 text-red-700" },
    expired: { label: "Expired", tone: "bg-gray-100 text-gray-600" },
    waiting: { label: "Waiting", tone: "bg-amber-100 text-amber-800" },
};

const CashApprovals: React.FC = () => {
    const { profile } = useAuth();
    const confirm = useConfirm();
    const [requests, setRequests] = useState<CashAuthRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState<number | null>(null);
    /** Redrawn on a timer so "12 min left" does not sit there going stale. */
    const [, setTick] = useState(0);

    const [history, setHistory] = useState<CashAuthHistoryRow[]>([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyFilter, setHistoryFilter] = useState<CashAuthOutcome | "all">("all");
    const [historyLoading, setHistoryLoading] = useState(true);
    const [historyError, setHistoryError] = useState("");

    const allowed = can(profile, "approvals");
    const editable = can(profile, "approvals", "manage");
    const firstLoad = useRef(true);

    const load = useCallback(async (quiet = false) => {
        if (!allowed) return;
        if (!quiet) setLoading(true);
        try {
            setRequests(await getApprovalQueue());
            setError("");
        } catch (e: any) {
            // A failed background refresh is not worth wiping the list for.
            if (!quiet) setError(errorOf(e, "Could not load the approvals."));
        } finally {
            setLoading(false);
            firstLoad.current = false;
        }
    }, [allowed]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!allowed) return;
        const t = setInterval(() => { load(true); setTick((n) => n + 1); }, POLL_MS);
        return () => clearInterval(t);
    }, [allowed, load]);

    /**
     * The record, which the queue above does not keep.
     *
     * Not polled. A decision that has already been taken does not change again,
     * and this is read deliberately rather than watched — so it refreshes when
     * asked, and after a decision made on this screen.
     */
    const loadHistory = useCallback(async (opts: { append?: boolean } = {}) => {
        if (!allowed) return;
        setHistoryLoading(true);
        try {
            const offset = opts.append ? history.length : 0;
            const page = await getApprovalHistory({
                outcome: historyFilter === "all" ? undefined : historyFilter,
                limit: HISTORY_PAGE,
                offset,
            });
            setHistory((prev) => (opts.append ? [...prev, ...page.requests] : page.requests));
            setHistoryTotal(page.total);
            setHistoryError("");
        } catch (e: any) {
            setHistoryError(errorOf(e, "Could not load the history."));
        } finally {
            setHistoryLoading(false);
        }
        // `history.length` is read for the offset but must not re-trigger this,
        // or appending a page would immediately ask for the one after it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed, historyFilter]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const decide = async (req: CashAuthRequest, approve: boolean) => {
        if (!approve) {
            const ok = await confirm({
                title: "Turn down this sale?",
                message: `${money(req.amount, req.currency)} — the agent will not be able to take ` +
                    `the payment, and there may be a customer at the counter.`,
                confirmLabel: "Turn it down",
                tone: "danger",
            });
            if (!ok) return;
        }

        setBusy(req.id);
        setError("");
        try {
            if (approve) await approveCashRequest(req.id);
            else await declineCashRequest(req.id);
            setRequests((prev) => prev.filter((r) => r.id !== req.id));
            // It leaves the queue and joins the record in the same moment, so
            // the decision never appears to vanish.
            void loadHistory();
        } catch (e: any) {
            // The server refuses an over-limit sale and says exactly what to do
            // about it. Show that verbatim and leave the request in the list,
            // because it is still waiting.
            setError(errorOf(e, "That decision could not be recorded."));
        } finally {
            setBusy(null);
        }
    };

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">Only head office can approve cash sales.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Cash approvals</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Sales where the code did not reach the agent's phone. Someone is probably
                        waiting at the counter.
                    </p>
                </div>
                <button
                    onClick={() => load()}
                    disabled={loading}
                    className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {loading && firstLoad.current && (
                    <div className="px-4 py-10 text-center text-gray-400">Loading…</div>
                )}

                {!loading && requests.length === 0 && (
                    <div className="px-4 py-10 text-center text-gray-400">
                        Nothing is waiting. Sales are going through on the code as they should.
                    </div>
                )}

                {requests.map((r) => (
                    <div key={r.id} className="px-4 py-4">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-gray-900">
                                        {money(r.amount, r.currency)}
                                    </span>
                                    <span className="text-xs text-gray-400">{r.bookingId}</span>
                                </div>

                                <p className="mt-1 text-sm text-gray-700">
                                    {r.agentName || r.agentEmail || r.uid}
                                    {r.agentName && r.agentEmail && (
                                        <span className="text-gray-400"> · {r.agentEmail}</span>
                                    )}
                                </p>

                                {r.reason && (
                                    <p className="mt-1 text-sm text-gray-500 italic">“{r.reason}”</p>
                                )}

                                <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-400">
                                    <FaClock className="text-[10px]" />
                                    Asked {waitingFor(r.requestedAt)} · {minutesLeft(r.expiresAt)}
                                </p>
                            </div>

                            {editable && (
                                <div className="flex gap-2 shrink-0">
                                    <button
                                        onClick={() => decide(r, true)}
                                        disabled={busy === r.id}
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        <FaCheck /> {busy === r.id ? "Working…" : "Approve"}
                                    </button>
                                    <button
                                        onClick={() => decide(r, false)}
                                        disabled={busy === r.id}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                    >
                                        <FaTimes /> Turn down
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <p className="mt-4 text-xs text-gray-500">
                Approving lets one sale through without the code. It does not raise anyone's daily
                limit — if an agent is already at their limit you will be told, and you can reset
                their day or change the limit from the staff screen first.
            </p>

            {/* ── The record ─────────────────────────────────────────────────
                Every request ever made, including the ones nobody answered.
                An approval is the only way past the payment code, so who took
                that route — and on whose word — is the thing worth keeping. */}
            <div className="mt-10">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                            <FaHistory className="text-gray-400" size={15} /> History
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Every request and what came of it. Nothing here is ever removed.
                        </p>
                    </div>

                    <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-1">
                        {HISTORY_FILTERS.map((f) => (
                            <button
                                key={f.value}
                                onClick={() => setHistoryFilter(f.value)}
                                className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                                    historyFilter === f.value
                                        ? "bg-gray-900 text-white"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {historyError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {historyError}
                    </div>
                )}

                <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {historyLoading && history.length === 0 && (
                        <div className="px-4 py-10 text-center text-gray-400">Loading…</div>
                    )}

                    {!historyLoading && history.length === 0 && (
                        <div className="px-4 py-10 text-center text-gray-400">
                            {historyFilter === "all"
                                ? "No approval has ever been asked for."
                                : "Nothing matches that filter."}
                        </div>
                    )}

                    {history.map((r) => {
                        const style = OUTCOME_STYLE[r.outcome];
                        return (
                            <div key={r.id} className="px-4 py-4">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold text-gray-900">
                                                {money(r.amount, r.currency)}
                                            </span>
                                            <span className="text-xs text-gray-400">{r.bookingId}</span>
                                        </div>

                                        <p className="mt-1 text-sm text-gray-700">
                                            {r.agentName || r.agentEmail || r.uid}
                                            {r.agentName && r.agentEmail && (
                                                <span className="text-gray-400"> · {r.agentEmail}</span>
                                            )}
                                        </p>

                                        {r.reason && (
                                            <p className="mt-1 text-sm text-gray-500 italic">“{r.reason}”</p>
                                        )}

                                        <p className="mt-1 text-xs text-gray-400">
                                            Asked {when(r.requestedAt)}
                                            {/* Who answered matters as much as the answer. An
                                                expired request has nobody to name. */}
                                            {r.outcome === "approved" || r.outcome === "declined" ? (
                                                <>
                                                    {" · "}
                                                    {style.label.toLowerCase()} by{" "}
                                                    {r.decidedByName || r.decidedBy || "someone"}{" "}
                                                    {when(r.decidedAt)}
                                                </>
                                            ) : r.outcome === "expired" ? (
                                                <> · nobody answered before {when(r.expiresAt)}</>
                                            ) : (
                                                <> · {minutesLeft(r.expiresAt)}</>
                                            )}
                                        </p>
                                    </div>

                                    <span
                                        className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${style.tone}`}
                                    >
                                        {style.label}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {history.length > 0 && (
                    <div className="mt-3 flex items-center justify-between gap-4">
                        <span className="text-xs text-gray-500">
                            Showing {history.length} of {historyTotal}
                        </span>
                        {history.length < historyTotal && (
                            <button
                                onClick={() => loadHistory({ append: true })}
                                disabled={historyLoading}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                            >
                                {historyLoading ? "Loading…" : "Load more"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CashApprovals;
